import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { Tile, isAmbientWalkPos, tileAt, type MapData, type ResourceNodeDef } from "@dogdell/shared";
import { applyLabelLod, NPC_LABEL_FAR, NPC_LABEL_NEAR } from "./LabelLod";
import { DOG_BREEDS } from "./DogBreeds";
import {
  billboardMaterial,
  faceCameraYaw,
  facingFlipSign,
  makeSilhouette,
  placeRimBehind,
  setSilhouetteMap,
  SPRITE_FOOT_Y,
  type WorldArt,
} from "./WorldArt";
import { HoverHighlight, makePickVolume } from "./HoverHighlight";

/**
 * Flavor NPC that only chats: wanders near home instead of standing frozen.
 * Talk is handled client-side (line toast); pick volume follows the pup.
 */
export class ParkNpc {
  readonly group = new THREE.Group();
  readonly hit: THREE.Mesh;
  readonly highlight: HoverHighlight;
  readonly nodeId: string;
  readonly line: string;
  private readonly sprite: THREE.Mesh;
  private readonly rim: THREE.Mesh;
  private readonly nameEl: HTMLDivElement;
  /** Fur index → breed via WorldArt (varies wanderer looks). */
  private readonly fur: number;
  private readonly homeX: number;
  private readonly homeY: number;
  private readonly roam: number;
  private readonly speed: number;
  private x: number;
  private y: number;
  private facing: number;
  private wanderAngle: number;
  private gaitPhase = 0;
  private frame = 0;
  private moving = false;
  private pauseUntil = 0;

  get poseX(): number {
    return this.x - 0.5;
  }

  get poseY(): number {
    return this.y - 0.5;
  }

  constructor(
    private readonly art: WorldArt,
    private readonly map: MapData,
    node: ResourceNodeDef,
    private readonly groundY: (poseX: number, poseY: number) => number,
  ) {
    this.nodeId = node.id;
    this.line = node.line ?? `${node.name} wags and wanders.`;
    this.fur = Math.floor(hash(node.id + "breed") * DOG_BREEDS.length);
    const home = snapAmbientHome(map, node.x + 0.5, node.y + 0.5);
    this.homeX = home.x;
    this.homeY = home.y;
    this.x = this.homeX;
    this.y = this.homeY;
    this.roam = 11 + hash(node.id) * 9;
    this.speed = 1.05 + hash(node.id + "spd") * 0.95;
    this.wanderAngle = hash(node.id) * Math.PI * 2;
    this.facing = this.wanderAngle;
    this.pauseUntil = performance.now() + hash(node.id + "wait") * 1800;

    const size = art.dogSize(this.fur);
    const mapTex = art.dog(this.fur, null, 0, undefined, "idle");
    this.rim = makeSilhouette(size.width, size.height, mapTex);
    this.rim.position.y = size.height * SPRITE_FOOT_Y;
    this.group.add(this.rim);
    this.sprite = new THREE.Mesh(
      new THREE.PlaneGeometry(size.width, size.height),
      billboardMaterial(mapTex),
    );
    this.sprite.position.y = size.height * SPRITE_FOOT_Y;
    this.sprite.renderOrder = 2;
    this.group.add(this.sprite);

    this.nameEl = document.createElement("div");
    this.nameEl.className = "world-label world-npc";
    this.nameEl.textContent = node.name;
    const nameObj = new CSS2DObject(this.nameEl);
    nameObj.position.set(0, size.height + 0.08, 0);
    this.group.add(nameObj);

    this.highlight = new HoverHighlight({
      billboard: { width: size.width, height: size.height, map: mapTex },
      facing: this.facing,
      // Dog textures already bake the cream pup rim.
      permanentRim: false,
    });
    this.group.add(this.highlight.group);

    this.hit = makePickVolume(Math.max(0.5, size.width * 0.35), size.height * 0.95);
    this.hit.position.y = 0;
    this.group.add(this.hit);

    this.place();
  }

  update(dtSec: number, now: number, camera: THREE.Camera, focusX: number, focusZ: number): void {
    if (now < this.pauseUntil) {
      this.moving = false;
    } else {
      this.wanderAngle += (hash(this.nodeId + Math.floor(now / 2200)) - 0.5) * dtSec * 1.8;
      const step = this.speed * dtSec;
      const wx = Math.cos(this.wanderAngle) * step;
      const wy = Math.sin(this.wanderAngle) * step;
      this.step(wx, wy);
      this.facing = this.wanderAngle;
      this.moving = true;

      const homeDist = Math.hypot(this.x - this.homeX, this.y - this.homeY);
      if (homeDist > this.roam) {
        const hx = ((this.homeX - this.x) / homeDist) * this.speed * dtSec * 1.35;
        const hy = ((this.homeY - this.y) / homeDist) * this.speed * dtSec * 1.35;
        if (!this.step(hx, hy)) {
          // Can't cut through blocked tiles — turn toward home instead.
          this.wanderAngle = Math.atan2(this.homeY - this.y, this.homeX - this.x);
        }
        this.facing = Math.atan2(this.homeY - this.y, this.homeX - this.x);
      }

      // Occasional pause to sniff / look around.
      if (hash(this.nodeId + Math.floor(now / 5000)) > 0.82) {
        this.pauseUntil = now + 900 + hash(this.nodeId + "p") * 1400;
      }
    }

    const size = this.art.dogSize(this.fur);
    if (this.moving) {
      this.gaitPhase += dtSec * 2.3 * Math.PI * 2;
      const next = 1 + (Math.floor((this.gaitPhase / (Math.PI * 2)) * 8) % 8);
      if (next !== this.frame) {
        this.frame = next;
        this.applyFrame(this.frame, "walk");
      }
    } else {
      this.gaitPhase += dtSec * 1.1;
      const idleFrame = Math.floor(now / 160 + hash(this.nodeId) * 10) % 10;
      if (idleFrame !== this.frame) {
        this.frame = idleFrame;
        this.applyFrame(this.frame, "idle");
      }
    }

    const bob = this.moving
      ? Math.pow(Math.sin(this.gaitPhase), 2) * 0.022
      : Math.sin(now * 0.0025) * 0.005;
    this.place();
    const feetY = size.height * SPRITE_FOOT_Y + bob;
    this.sprite.position.set(0, feetY, 0);
    this.highlight.group.position.set(0, 0, 0);
    this.highlight.setFacing(this.facing);
    this.highlight.update(now, camera);

    faceCameraYaw(this.sprite, camera);
    const flip = facingFlipSign(this.x, this.y, this.facing, camera);
    this.sprite.scale.set(flip, 1, 1);
    placeRimBehind(this.rim, this.sprite, flip, 1);

    const dist = Math.hypot(this.x - focusX, this.y - focusZ);
    applyLabelLod(this.nameEl, dist, { near: NPC_LABEL_NEAR, far: NPC_LABEL_FAR });
  }

  destroy(): void {
    this.highlight.destroy();
    this.nameEl.remove();
    this.group.traverse((child) => {
      if (child instanceof CSS2DObject) {
        child.element.remove();
      }
      if (child instanceof THREE.Mesh && child !== this.hit) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
    this.hit.geometry.dispose();
    if (this.hit.material instanceof THREE.Material) {
      this.hit.material.dispose();
    }
    this.group.removeFromParent();
  }

  private applyFrame(frame: number, anim: "idle" | "walk"): void {
    const map = this.art.dog(this.fur, null, frame, undefined, anim);
    const mat = this.sprite.material as THREE.MeshBasicMaterial;
    mat.map = map;
    mat.needsUpdate = true;
    setSilhouetteMap(this.rim, map);
    this.highlight.setMap(map);
  }

  private place(): void {
    const gy = this.groundY(this.x - 0.5, this.y - 0.5);
    this.group.position.set(this.x, gy, this.y);
  }

  /** Returns false when the step was rejected (caller may redirect). */
  private step(dx: number, dy: number): boolean {
    const nx = this.x + dx;
    const ny = this.y + dy;
    if (!isAmbientWalkPos(this.map, nx, ny)) {
      this.wanderAngle += Math.PI * 0.55;
      return false;
    }
    const tile = tileAt(this.map, Math.floor(nx), Math.floor(ny));
    if (tile === Tile.Water) {
      this.wanderAngle += Math.PI * 0.55;
      return false;
    }
    this.x = nx;
    this.y = ny;
    return true;
  }
}

function snapAmbientHome(map: MapData, x: number, y: number): { x: number; y: number } {
  if (isAmbientWalkPos(map, x, y)) {
    return { x, y };
  }
  const cx = Math.round(x);
  const cy = Math.round(y);
  for (let r = 1; r <= 12; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
          continue;
        }
        const px = cx + dx;
        const py = cy + dy;
        if (isAmbientWalkPos(map, px, py)) {
          return { x: px, y: py };
        }
      }
    }
  }
  return { x, y };
}

function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}
